// src/context/ThemeContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { lightTheme, darkTheme } from '../style/styles';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Check local storage or system preference for initial theme
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : false;
  });

  // Transliteration State
  const [isTransliterationEnabled, setIsTransliterationEnabled] = useState(() => {
    const saved = localStorage.getItem('transliteration');
    return saved ? saved === 'true' : true; // Default to true (Marathi)
  });

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const toggleTransliteration = () => {
    setIsTransliterationEnabled(prev => !prev);
  };

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');

    // Apply body gradient classes
    const lightGradient = "bg-gradient-to-br from-lime-50 via-ayurmax-primary to-ayurmax-dark min-h-screen bg-fixed bg-no-repeat";
    const darkGradient = "bg-gradient-to-br from-ayurmax-dark via-black to-ayurmax-dark min-h-screen bg-fixed bg-no-repeat text-white";

    // Remove old inline styles if any
    document.body.style.backgroundColor = "";
    document.body.style.color = "";

    // Reset base classes
    document.body.className = "transition-colors duration-500 ease-in-out";

    if (isDarkMode) {
      document.body.classList.add(...darkGradient.split(" "));
    } else {
      document.body.classList.add(...lightGradient.split(" "));
      document.body.style.color = lightTheme.text; // Keep text color for light mode as gradient doesn't set it
    }

    // Toggle 'dark' class on html element for tailwind dark: classes
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('transliteration', isTransliterationEnabled);
  }, [isTransliterationEnabled]);

  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{
      theme,
      isDarkMode,
      toggleTheme,
      isTransliterationEnabled,
      toggleTransliteration
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};